import { DeleteOutlineOutlined } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  Grid2,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { notifyError, notifySuccess } from "../../utilities/helper";
import {
  deleteCapService,
  updateCapsService,
} from "../../services/studyCapsService";
import { capLevelTypeIds } from "../../pages/StudyCaps/StudyCaps";

const CapsTableComponent = ({
  rows,
  setRows,
  isFullScreen,
  getOnLoadData = null,
  setLoad = null,
  siteId = null,
  isNotStatusCap = false,
  dynamicColName = "",
  columnValue = "",
  tabValue,
  apiName = null,
  isFromSiteCaps = false,
}) => {
  const [deletedId, setDeletedId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const roleId = localStorage.getItem("roleId");

  const access_token = localStorage.getItem("access_token");
  const navigate = useNavigate();

  // const handleChange = (index, field, value) => {
  //   const updatedRows = [...rows];
  //   updatedRows[index][field] = value;
  //   setRows(updatedRows);
  // };

  const handleChange = (index, field, value) => {
    let sanitizedValue = value;

    if (typeof value === "string") {
      // 1️⃣ Remove leading/trailing spaces
      const trimmed = value.trim();

      // 2️⃣ If only spaces → make empty
      if (trimmed === "") {
        sanitizedValue = "";
      }
      // 3️⃣ Allow only whole numbers (NO decimals)
      else if (/^\d+$/.test(trimmed)) {
        const numberValue = parseInt(trimmed, 10);

        if (numberValue >= 0 && numberValue <= 10000) {
          sanitizedValue = trimmed;
        } else {
          return; // ❌ block invalid range
        }
      } else {
        return; // ❌ block decimals, alphabets, symbols etc.
      }
    }

    const updatedRows = [...rows];
    updatedRows[index][field] = sanitizedValue;
    setRows(updatedRows);
  };

  const removeRow = async () => {
    if (isFromSiteCaps) {
      try {
        setLoad(true);
        const res = await apiName(
          access_token,
          roleId,
          deletedId,
          siteId,
          localStorage.getItem("globalDateFormat"),
        );
        if (res?.status === 200) {
          notifySuccess("Cap detail deleted successfully");
          getOnLoadData(siteId);
        }
      } catch (err) {
        console.error("Error deleting cap detail:", err);
        notifyError(err?.message || "Something went wrong!");
      } finally {
        setLoad(false);
        setShowConfirmModal(false);
        setDeletedId(null);
      }
    } else {
      try {
        setLoad(true);
        const res = await deleteCapService(
          access_token,
          deletedId,
          capLevelTypeIds[tabValue],
          null,
          roleId,
          localStorage.getItem("globalDateFormat"),
        );
        if (res?.status === 200) {
          setLoad(false);
          getOnLoadData(tabValue);
          setShowConfirmModal(false);
          setDeletedId(null);
          notifySuccess("Cap detail deleted successfully");
        }
      } catch (err) {
        setLoad(false);
        setShowConfirmModal(false);
        setDeletedId(null);
        console.error("Error deleting cap detail:", err);
        if (err?.status === 401) {
          navigate("/unAuthorize");
        }
      }
    }
  };

  const updateCaps = async () => {
    try {
      setLoad(true);
      const payload = rows.map((row) => ({
        id: row.id,
        alertCapCount: row.alertCapCount,
        stopCapCount: row.stopCapCount,
        targetCount: row.targetCount,
      }));
      const res = await updateCapsService(
        access_token,
        capLevelTypeIds[tabValue],
        null,
        roleId,
        localStorage.getItem("globalDateFormat"),
        payload,
      );
      if (res?.status === 200) {
        notifySuccess("Caps updated successfully!");
        getOnLoadData(tabValue);
      }
    } catch (err) {
      console.log("Error in updating caps : ", err);
      notifyError(err?.message || "Something went wrong!");
      if (err?.status === 401) {
        navigate("/unAuthorize");
      }
    } finally {
      setLoad(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: isFullScreen ? "100%" : "70%",
      }}
    >
      <div
        // style={{
        //   width: isFullScreen ? "100%" : "70%",
        //   border: "1px solid lightgrey",
        //   borderRadius: "8px",
        //   padding: "5px",
        //   overflowX: "auto", // just in case screen is too small, allow horizontal scroll
        //   // maxHeight: "300px",
        //   // overflowY: "auto",
        // }}
        style={{
          width: "100%",
          border: "1px solid lightgrey",
          borderRadius: "8px",
          padding: "5px",
          overflowX: "auto",
        }}
      >
        {rows?.length > 0 ? (
          <div>
            <Grid2
              container
              spacing={5}
              alignItems="center"
              wrap="nowrap" // ✅ prevents wrapping
              sx={{
                margin: "20px 10px",
                flexWrap: "nowrap",
              }}
            >
              {/* Dynamic Col Label */}
              {isNotStatusCap && (
                <Grid2
                  item
                  sx={{
                    width: isFullScreen && isNotStatusCap ? "19vw" : "5vw",
                    flexShrink: 0, // ✅ prevents shrinking
                  }}
                >
                  <div style={{ fontWeight: "bold" }}>
                    {dynamicColName
                      ?.split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")}
                  </div>
                </Grid2>
              )}

              {/* Status Label */}
              <Grid2
                item
                sx={{
                  width: isFullScreen && isNotStatusCap ? "19vw" : "5vw",
                  flexShrink: 0, // ✅ prevents shrinking
                }}
              >
                <div style={{ fontWeight: "bold" }}>Status</div>
              </Grid2>

              <Grid2
                item
                sx={{
                  width: isFullScreen && isNotStatusCap ? "19vw" : "10.5vw",
                  flexShrink: 0, // ✅ prevents shrinking
                }}
              >
                <div style={{ fontWeight: "bold" }}>Alert Level</div>
              </Grid2>
              <Grid2
                item
                sx={{
                  width: isFullScreen && isNotStatusCap ? "19vw" : "10.5vw",
                  flexShrink: 0, // ✅ prevents shrinking
                }}
              >
                <div style={{ fontWeight: "bold" }}>Cap Level</div>
              </Grid2>
              <Grid2
                item
                sx={{
                  width: isFullScreen && isNotStatusCap ? "19vw" : "10.5vw",
                  flexShrink: 0, // ✅ prevents shrinking
                }}
              >
                <div style={{ fontWeight: "bold" }}>Target Level</div>
              </Grid2>
            </Grid2>
            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                background: "transparent",
              }}
            >
              {rows.map((row, index) => (
                <Grid2
                  container
                  spacing={5}
                  key={index}
                  alignItems="center"
                  wrap="nowrap" // ✅ prevents wrapping
                  sx={{
                    margin: "20px 10px",
                    flexWrap: "nowrap",
                  }}
                >
                  {isNotStatusCap && (
                    <Grid2
                      item
                      sx={{
                        width: isFullScreen && isNotStatusCap ? "19vw" : "5vw",
                        flexShrink: 0, // ✅ prevents shrinking
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>
                        {row[columnValue] || row?.patientStatusTypeName}
                      </div>
                    </Grid2>
                  )}
                  {/* Status Label */}
                  <Grid2
                    item
                    sx={{
                      width: isFullScreen && isNotStatusCap ? "19vw" : "5vw",
                      flexShrink: 0, // ✅ prevents shrinking
                    }}
                  >
                    <div style={{ fontWeight: "bold" }}>
                      {row.status || row?.patientStatusTypeName}
                    </div>
                  </Grid2>

                  {/* Alert Level */}
                  <Grid2
                    item
                    sx={{
                      width: isFullScreen && isNotStatusCap ? "19vw" : "10.5vw",
                      flexShrink: 0,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.alertCapCount}
                      onChange={(e) => {
                        const value = e.target.value;

                        // Allow empty string (so user can delete input)
                        if (value === "") {
                          handleChange(index, "alertCapCount", "");
                          return;
                        }

                        // Convert to number
                        const numberValue = Number(value);

                        // Check if it's a valid number and non-negative
                        if (
                          !isNaN(numberValue) &&
                          numberValue >= 0 &&
                          numberValue <= 10000
                        ) {
                          handleChange(index, "alertCapCount", value);
                        }
                      }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }} // mobile-friendly numeric keyboard
                    />
                  </Grid2>

                  {/* Cap Level */}
                  <Grid2
                    item
                    sx={{
                      width: isFullScreen && isNotStatusCap ? "19vw" : "10.5vw",
                      flexShrink: 0,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.stopCapCount}
                      onChange={(e) => {
                        const value = e.target.value;

                        // Allow empty input so user can clear the field
                        if (value === "") {
                          handleChange(index, "stopCapCount", "");
                          return;
                        }

                        // Convert to number
                        const numberValue = Number(value);

                        // Allow only valid non-negative numbers
                        if (
                          !isNaN(numberValue) &&
                          numberValue >= 0 &&
                          numberValue <= 10000
                        ) {
                          handleChange(index, "stopCapCount", value);
                        }
                      }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    />
                  </Grid2>

                  {/* Target Level */}
                  <Grid2
                    item
                    sx={{
                      width: isFullScreen && isNotStatusCap ? "19vw" : "10.5vw",
                      flexShrink: 0,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={row.targetCount}
                      onChange={(e) => {
                        const value = e.target.value;

                        // Allow clearing the input
                        if (value === "") {
                          handleChange(index, "targetCount", "");
                          return;
                        }

                        // Convert to number
                        const numberValue = Number(value);

                        // Allow only valid non-negative numbers
                        if (
                          !isNaN(numberValue) &&
                          numberValue >= 0 &&
                          numberValue <= 10000
                        ) {
                          handleChange(index, "targetCount", value);
                        }
                      }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    />
                  </Grid2>

                  {/* Delete Icon */}
                  <Grid2
                    item
                    sx={{
                      width: "3vw",
                      flexShrink: 0,
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        background: "red",
                        padding: "15px",
                      }}
                      className="grid-edit-btn"
                      onClick={() => {
                        setShowConfirmModal(true);
                        setDeletedId(row?.id);
                      }}
                    >
                      <DeleteOutlineOutlined sx={{ color: "white" }} />
                    </button>
                  </Grid2>
                </Grid2>
              ))}
            </div>
          </div>
        ) : (
          <h3
            style={{
              color: "grey",
              textAlign: "center",
            }}
          >
            No Caps Added
          </h3>
        )}
      </div>

      {rows && rows?.length > 0 && !isFullScreen && (
        <div
          style={{
            width: "100%",
            marginTop: "12px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            className="create-btn"
            style={{ width: "150px" }}
            onClick={updateCaps}
          >
            Save
          </button>
        </div>
      )}

      {/* Some where here in the end */}
      {showConfirmModal && (
        <Dialog
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
        >
          <div
            style={{
              width: "400px",
              padding: "20px",
            }}
          >
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Delete Cap Detail
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
              Are you sure you want to delete this detail ?
            </Typography>
          </div>
          <DialogActions>
            <button
              className="create-btn"
              style={{ width: "120px" }}
              onClick={removeRow}
            >
              Yes
            </button>
            <button
              className="create-btn-outlined"
              style={{ width: "120px" }}
              onClick={() => setShowConfirmModal(false)}
            >
              No
            </button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default CapsTableComponent;
